/**
 * Dataset Assistant V2 component.
 *
 * In compact mode (sidebar), renders a 52px toolbar with a dropdown
 * ConversationSelector, new-analysis button, and custom header actions.
 * In fullscreen mode (!compactMode), renders a full left-hand
 * ConversationSidebar and a minimal header with only the header actions.
 */
import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { HttpAgent } from "@ag-ui/client";
import { AssistantRuntimeProvider } from "@assistant-ui/react";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { Loader2, Plus, ServerCrash } from "lucide-react";
import { ShareIcon } from "~/assets/icons";
import { Thread } from "~/components/assistant-v2/Thread";
import { useAssistantFeatures } from "~/components/assistant-v2/useAssistantFeatures";
import { useContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useConversationName } from "~/components/assistant-v2/useConversationName";
import { useStreamingTurnIds } from "~/components/assistant-v2/useMessageFeedback";
import { useRunError } from "~/components/assistant-v2/useRunError";
import { useSkillSync } from "~/components/assistant-v2/useSkillSync";
import { useStreamingArtifacts } from "~/components/assistant-v2/useStreamingArtifacts";
import { AssistantProvider } from "~/components/assistant/AssistantContext";
import { ConversationSelector } from "~/components/assistant/ConversationSelector";
import { IconButton } from "~/components/assistant/PopoverButton";
import { ShareConversationModal } from "~/components/assistant/ShareConversationModal";
import { filterVisibleServers } from "~/components/assistant/hooks/useMcpServers";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";
import { ConversationSidebar } from "./ConversationSidebar";
import { useConversationHistory } from "./useConversationHistory";
import { useConversationManager } from "./useConversationManager";
import { useRunCancellation } from "./useRunCancellation";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

export interface DatasetAssistantV2Props {
  datasetId: string;
  className?: string;
  headerActions?: ReactNode;
  compactMode?: boolean;
}

// @lat: [[frontend/ai-assistant#V2 AG-UI Assistant]]
export function DatasetAssistantV2({
  datasetId,
  className,
  headerActions,
  compactMode = false,
}: DatasetAssistantV2Props) {
  const user = useUser();
  const debugMode = useFliptBoolean("agent-debug-mode", false, user?.email);
  const { data: dataset } = useDataset(datasetId);
  const datasetTitle = dataset?.title ?? datasetId;

  const suggestions = useMemo(() => {
    const prompts = dataset?.samplePrompts
      ?.filter((p) => p.trim().length > 0)
      .map((p) => ({ title: p, prompt: p }));
    return prompts && prompts.length > 0 ? prompts : undefined;
  }, [dataset?.samplePrompts]);

  const conversationShortcuts = dataset?.conversationShortcuts ?? [];

  const {
    conversations,
    currentConversationId,
    isLoading: isLoadingConversations,
    isCreating,
    loadError,
    createConversation,
    deleteConversation,
    selectConversation,
    renameConversation,
    updateConversationName,
  } = useConversationManager({ api, datasetId });

  const {
    skillsEnabled,
    mcpServers,
    selectedMcpServers,
    toggleMcpServer,
    systemSkills,
    userSkills,
    datasetSkills,
    isLoadingSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    getSkill,
    refreshSkills,
  } = useAssistantFeatures({ api, contextId: datasetId, enableSkills: true });

  const visibleServers = useMemo(
    () => filterVisibleServers(mcpServers, dataset?.externalMcpAccess ?? []),
    [mcpServers, dataset?.externalMcpAccess]
  );

  const agent = useMemo(() => {
    if (!currentConversationId) {
      return new HttpAgent({
        url: "about:blank",
        headers: { Accept: "text/event-stream" },
      });
    }
    return new HttpAgent({
      url: `${VITE_ANALYSIS_AGENT_API}/conversations/${currentConversationId}/messages_agui`,
      headers: { Accept: "text/event-stream" },
    });
  }, [currentConversationId]);

  const runtime = useAgUiRuntime({
    agent,
    showThinking: true,
    logger: {
      debug: (...args: any[]) => console.debug("[AG-UI]", ...args),
      error: (...args: any[]) => console.error("[AG-UI]", ...args),
    },
  });

  const sendPrompt = useCallback(
    (prompt: string) => {
      runtime.thread.append({
        role: "user",
        content: [{ type: "text", text: prompt }],
      });
    },
    [runtime]
  );

  const contextBuffer = useContextBuffer(agent);
  const streamingArtifactNames = useStreamingArtifacts(agent);
  const { error: runError, clearError } = useRunError(agent);
  const streamingTurnIds = useStreamingTurnIds(agent, runtime);
  useConversationName(agent, updateConversationName);
  useSkillSync(agent, refreshSkills);
  const { isLoadingHistory } = useConversationHistory(runtime, currentConversationId);
  const cancellation = useRunCancellation({
    api,
    agent,
    runtime,
    conversationId: currentConversationId,
    clearError,
  });

  useEffect(() => {
    trackEvent("assistant_v2_ui_opened", {
      dataset_id: datasetId,
      context: "dataset",
    });
    return () => {
      trackEvent("assistant_v2_ui_closed", {
        dataset_id: datasetId,
        context: "dataset",
      });
    };
  }, [datasetId]);

  const handleNewChat = useCallback(async () => {
    trackEvent("assistant_v2_new_chat_clicked", {
      dataset_id: datasetId,
      context: "dataset",
    });
    await createConversation();
  }, [datasetId, createConversation]);

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      if (conversationId === currentConversationId) {
        return;
      }
      trackEvent("assistant_v2_conversation_selected", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      selectConversation(conversationId);
    },
    [datasetId, currentConversationId, selectConversation]
  );

  const handleDeleteConversation = useCallback(
    (conversationId: string, event: React.MouseEvent) => {
      trackEvent("assistant_v2_conversation_deleted", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      deleteConversation(conversationId, event);
    },
    [datasetId, deleteConversation]
  );

  const handleRenameConversation = useCallback(
    (conversationId: string, displayName: string) => {
      trackEvent("assistant_v2_conversation_renamed", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      renameConversation(conversationId, displayName);
    },
    [datasetId, renameConversation]
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      trackEvent("assistant_v2_sidebar_toggled", {
        dataset_id: datasetId,
        context: "dataset",
        collapsed: !prev,
      });
      return !prev;
    });
  }, [datasetId]);

  if (isLoadingConversations) {
    return (
      <div className={cn("flex h-full items-center justify-center bg-white", className)}>
        <Loader2 className="size-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={cn("flex h-full items-center justify-center bg-white", className)}>
        <div className="flex flex-col items-center gap-3 text-center">
          <ServerCrash className="size-10 text-gray-400" />
          <p className="text-lg font-medium text-gray-700">Analysis Agent is unavailable</p>
          <p className="text-sm text-gray-500">
            Please try again later or contact support if the issue persists.
          </p>
        </div>
      </div>
    );
  }

  const threadContent = (
    <AssistantProvider
      api={api}
      conversationId={currentConversationId}
      contextId={datasetId}
      debugMode={debugMode}
      sendPrompt={sendPrompt}
    >
      {currentConversationId ? (
        <AssistantRuntimeProvider runtime={runtime}>
          <Thread
            key={currentConversationId}
            conversationId={currentConversationId}
            contextId={datasetId}
            suggestions={suggestions}
            contextBuffer={contextBuffer}
            isLoadingHistory={isLoadingHistory}
            runError={runError}
            streamingTurnIds={streamingTurnIds}
            streamingArtifactNames={streamingArtifactNames}
            {...cancellation}
            skillsEnabled={skillsEnabled}
            systemSkills={systemSkills}
            userSkills={userSkills}
            datasetSkills={datasetSkills}
            isLoadingSkills={isLoadingSkills}
            onCreateSkill={createSkill}
            onUpdateSkill={updateSkill}
            onDeleteSkill={deleteSkill}
            onGetSkill={getSkill}
            mcpServers={visibleServers}
            selectedMcpServers={selectedMcpServers}
            onToggleMcpServer={toggleMcpServer}
            showToolSelector={true}
            placeholder={`Ask anything about ${datasetTitle}...`}
          />
        </AssistantRuntimeProvider>
      ) : null}
    </AssistantProvider>
  );

  return (
    <div className={cn("flex h-full bg-white", compactMode ? "flex-col" : "flex-row", className)}>
      {!compactMode && (
        <ConversationSidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          conversationShortcuts={conversationShortcuts}
          onNewChat={handleNewChat}
          onSelect={handleConversationSelect}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
          onToggle={handleToggleSidebar}
          isCollapsed={isSidebarCollapsed}
        />
      )}

      <div className="flex flex-col h-full min-w-0 w-full">
        <div className="flex flex-row items-center justify-between px-4 h-[52px] min-h-[52px] shrink-0 w-full border-b border-black/[0.12]">
          {compactMode ? (
            <div className="flex flex-row gap-0.5 items-center">
              <ConversationSelector
                conversations={conversations}
                currentConversationId={currentConversationId}
                conversationShortcuts={conversationShortcuts}
                isCreatingConversation={isCreating}
                onSelect={handleConversationSelect}
                onDelete={handleDeleteConversation}
                onRename={handleRenameConversation}
              />
            </div>
          ) : (
            <p className="text-sm font-medium text-gray-700 truncate">
              {conversations.find((c) => c.id === currentConversationId)?.display_name ?? ""}
            </p>
          )}

          <div className="flex flex-row gap-0.5 items-center justify-end">
            {compactMode && (
              <>
                <Tooltip delayDuration={700}>
                  <TooltipTrigger asChild>
                    <IconButton
                      onClick={handleNewChat}
                      disabled={isCreating}
                      variant="ghost"
                      aria-label="New Analysis"
                    >
                      <Plus className="size-4.5 text-gray-600" />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New Analysis</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={700}>
                  <TooltipTrigger asChild>
                    <IconButton
                      onClick={() => setShareModalOpen(true)}
                      variant="ghost"
                      aria-label="Share Analysis"
                      disabled={!currentConversationId}
                    >
                      <ShareIcon className="size-4.5 text-gray-600" />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Share Analysis</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            {!compactMode && (
              <div className="mr-2">
                <Button
                  onClick={() => setShareModalOpen(true)}
                  disabled={!currentConversationId}
                  variant="outline"
                  size="sm"
                >
                  Share
                </Button>
              </div>
            )}
            {headerActions}
          </div>
        </div>

        <div className="flex-1 min-h-0">{threadContent}</div>
      </div>

      {currentConversationId && (
        <ShareConversationModal
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          conversationId={currentConversationId}
        />
      )}
    </div>
  );
}
